import React, { Component, ErrorInfo, ReactNode } from "react";
import { SUPPORT_EMAIL } from "../brand";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * App-level Error Boundary
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire app (white screen).
 */
class AppErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log the error to console for debugging
    console.error("App Error Boundary caught an error:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleRefresh = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
            backgroundColor: "#f8f9fa",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          }}
        >
          <div
            style={{
              maxWidth: "500px",
              textAlign: "center",
              backgroundColor: "white",
              padding: "40px",
              borderRadius: "12px",
              boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            }}
          >
            <div
              style={{
                fontSize: "48px",
                marginBottom: "20px",
              }}
            >
              😕
            </div>
            <h1
              style={{
                color: "#333",
                fontSize: "24px",
                marginBottom: "12px",
                fontWeight: 600,
              }}
            >
              Something went wrong
            </h1>
            <p
              style={{
                color: "#666",
                fontSize: "16px",
                marginBottom: "24px",
                lineHeight: 1.5,
              }}
            >
              We're sorry, but something unexpected happened. Please try
              refreshing the page or go back to the home page.
            </p>
            <button
              onClick={this.handleRefresh}
              style={{
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: 500,
                color: "white",
                backgroundColor: "#01b88e",
                border: "none",
                borderRadius: "6px",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={e =>
                (e.currentTarget.style.backgroundColor = "#019d79")
              }
              onMouseOut={e =>
                (e.currentTarget.style.backgroundColor = "#01b88e")
              }
            >
              Refresh Page
            </button>
            <p
              style={{
                marginTop: "20px",
                color: "#666",
                fontSize: "14px",
              }}
            >
              If this error persists, please email{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                style={{ color: "#01b88e", textDecoration: "none" }}
              >
                {SUPPORT_EMAIL}
              </a>
            </p>
            {process.env.NODE_ENV === "development" && this.state.error && (
              <details
                style={{
                  marginTop: "24px",
                  textAlign: "left",
                  backgroundColor: "#f8f9fa",
                  padding: "12px",
                  borderRadius: "6px",
                  fontSize: "12px",
                }}
              >
                <summary
                  style={{
                    cursor: "pointer",
                    color: "#666",
                    marginBottom: "8px",
                  }}
                >
                  Error Details (Development Only)
                </summary>
                <pre
                  style={{
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    color: "#dc3545",
                    margin: 0,
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppErrorBoundary;
